import { useCallback, useMemo, useState, type ReactElement } from "react";
import { Plus } from "lucide-react";
import { Button, ConfirmDialog, SkeletonRows } from "@primitives/index";
import { AdminSection, ErrorAlert, MutationError } from "./admin-ui";
import { useMyQueues } from "./queues";
import {
    RuleEditor,
    RuleRow,
    emptyForm,
    filterRulesForQueue,
    fromRule,
    toPayload,
    useCreateRoutingRule,
    useDeleteRoutingRule,
    useRoutingRules,
    useRoutingTagKeys,
    useSetRoutingRuleEnabled,
    useUpdateRoutingRule,
} from "./routing";
import type { RoutingRule, RuleFormState } from "./routing";
import { isRuleRoutableKind, toTaskTypeOptions, useTaskTypes, useWorkflowKinds } from "./workflows";

// Rules feeding a single queue, presented on that queue's detail page.
//
// Single-target rules make this section the natural home for "rules
// that direct work HERE": filter the master list by targetQueueId and
// surface every CRUD affordance the master routing-rules page offers,
// minus retargeting (intentionally still a master-page concern so the
// queue page's mental model stays "rules for THIS queue").
//
// The section also tolerates a read-only viewer: non-admin members
// still see the rules so they understand why work lands here.

interface QueueRoutingRulesSectionProps {
    readonly queueId: string;
    readonly canEdit: boolean;
}

type EditorState =
    | { readonly mode: "idle" }
    | { readonly mode: "create"; readonly form: RuleFormState }
    | { readonly mode: "edit"; readonly ruleId: string; readonly form: RuleFormState };

export function QueueRoutingRulesSection({
    queueId,
    canEdit,
}: QueueRoutingRulesSectionProps): ReactElement {
    const rulesQuery = useRoutingRules();
    const tagKeysQuery = useRoutingTagKeys();
    const kindsQuery = useWorkflowKinds();
    const taskTypesQuery = useTaskTypes();
    const queuesQuery = useMyQueues();

    const createMutation = useCreateRoutingRule();
    const updateMutation = useUpdateRoutingRule();
    const enableMutation = useSetRoutingRuleEnabled();
    const deleteMutation = useDeleteRoutingRule();

    const [editor, setEditor] = useState<EditorState>({ mode: "idle" });
    const [pendingDelete, setPendingDelete] = useState<RoutingRule | null>(null);

    const allRules = useMemo(() => rulesQuery.data?.items ?? [], [rulesQuery.data]);
    const queues = useMemo(() => queuesQuery.data?.items ?? [], [queuesQuery.data]);
    const availableTagKeys = useMemo(
        () => tagKeysQuery.data?.keys ?? [],
        [tagKeysQuery.data],
    );
    const availableKinds = useMemo(
        () => (kindsQuery.data?.kinds ?? []).filter(isRuleRoutableKind),
        [kindsQuery.data],
    );
    const availableTaskTypes = useMemo(
        () => toTaskTypeOptions(taskTypesQuery.data?.taskTypes ?? []),
        [taskTypesQuery.data],
    );

    const queueNamesById = useMemo(() => {
        const map = new Map<string, string>();
        for (const q of queues) {
            map.set(q.id, q.name);
        }
        return map;
    }, [queues]);

    const queueRules = useMemo(
        () => filterRulesForQueue(allRules, queueId),
        [allRules, queueId],
    );

    const beginCreate = useCallback(() => {
        setEditor({
            mode: "create",
            form: { ...emptyForm(), targetQueueId: queueId },
        });
    }, [queueId]);

    const beginEdit = useCallback((rule: RoutingRule) => {
        setEditor({ mode: "edit", ruleId: rule.id, form: fromRule(rule) });
    }, []);

    const cancelEdit = useCallback(() => {
        setEditor({ mode: "idle" });
        createMutation.reset();
        updateMutation.reset();
    }, [createMutation, updateMutation]);

    const handleSubmit = useCallback(() => {
        if (editor.mode === "idle") return;
        // Belt-and-braces: re-pin the target to this queue on submit.
        // The editor's locked-target UI prevents the field from ever
        // being changed in this flow, but pinning here also defends
        // against a future refactor that allows the form to mutate
        // targetQueueId in unexpected ways.
        const payload = toPayload({ ...editor.form, targetQueueId: queueId });
        if (editor.mode === "create") {
            createMutation.mutate(
                {
                    workflowKind: payload.workflowKind,
                    entityType: payload.entityType,
                    taskType: payload.taskType,
                    description: payload.description,
                    tagPredicates: payload.tagPredicates,
                    targetQueueId: payload.targetQueueId,
                },
                {
                    onSuccess: () => setEditor({ mode: "idle" }),
                },
            );
        } else {
            updateMutation.mutate(
                {
                    id: editor.ruleId,
                    entityType: payload.entityType,
                    taskType: payload.taskType,
                    description: payload.description,
                    tagPredicates: payload.tagPredicates,
                    targetQueueId: payload.targetQueueId,
                },
                {
                    onSuccess: () => setEditor({ mode: "idle" }),
                },
            );
        }
    }, [editor, queueId, createMutation, updateMutation]);

    const handleToggle = useCallback(
        (rule: RoutingRule) => {
            enableMutation.mutate({ id: rule.id, enabled: !rule.isEnabled });
        },
        [enableMutation],
    );

    const confirmDelete = useCallback(() => {
        if (pendingDelete === null) return;
        deleteMutation.mutate(pendingDelete.id);
        setPendingDelete(null);
    }, [deleteMutation, pendingDelete]);

    const description =
        queueRules.length === 0
            ? "No routing rules direct work items to this queue."
            : `${queueRules.length} rule${queueRules.length === 1 ? "" : "s"} feed${queueRules.length === 1 ? "s" : ""} this queue.`;

    return (
        <AdminSection
            title="Routing rules"
            description={description}
            trailing={
                canEdit && editor.mode === "idle" ? (
                    <Button variant="primary" size="sm" onClick={beginCreate}>
                        <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
                        Add rule
                    </Button>
                ) : undefined
            }
        >
            {editor.mode !== "idle" && (
                <RuleEditor
                    mode={editor.mode}
                    form={editor.form}
                    onChange={(next) => {
                        setEditor((s) =>
                            s.mode === "idle" ? s : { ...s, form: next },
                        );
                    }}
                    availableKinds={availableKinds}
                    availableTaskTypes={availableTaskTypes}
                    availableTagKeys={availableTagKeys}
                    availableQueues={queues}
                    isSubmitting={createMutation.isPending || updateMutation.isPending}
                    submitError={
                        editor.mode === "create"
                            ? createMutation.error
                            : updateMutation.error
                    }
                    onSubmit={handleSubmit}
                    onCancel={cancelEdit}
                    lockedTargetQueueId={queueId}
                />
            )}

            {rulesQuery.isLoading && (
                <SkeletonRows rows={2} rowHeight={96} shape="card" label="Loading routing rules" />
            )}
            {rulesQuery.error !== null && (
                <ErrorAlert onRetry={() => void rulesQuery.refetch()}>
                    Couldn&rsquo;t load routing rules. {rulesQuery.error.message}
                </ErrorAlert>
            )}
            <MutationError error={enableMutation.error ?? deleteMutation.error} />
            {queueRules.map((r) => {
                const isToggling =
                    enableMutation.isPending && enableMutation.variables?.id === r.id;
                const isDeleting =
                    deleteMutation.isPending && deleteMutation.variables === r.id;
                return (
                    <RuleRow
                        key={r.id}
                        rule={r}
                        queueNamesById={queueNamesById}
                        isToggling={isToggling}
                        isDeleting={isDeleting}
                        onEdit={() => beginEdit(r)}
                        onToggleEnabled={() => handleToggle(r)}
                        onDelete={() => setPendingDelete(r)}
                        canEdit={canEdit}
                        hideTargetQueue
                    />
                );
            })}

            <ConfirmDialog
                open={pendingDelete !== null}
                title="Delete rule"
                message={`Delete rule "${pendingDelete?.description ?? ""}"? This cannot be undone.`}
                confirmLabel="Delete"
                tone="danger"
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </AdminSection>
    );
}
